import axios from "axios";

export const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

// Global 401 handling: any protected endpoint returning 401 means the session
// expired mid-flight. Instead of letting axios reject (which would surface as a
// CRA runtime-error overlay for any caller that forgot to `.catch`), we dispatch
// a window event that AuthContext listens for, and return a never-resolving
// promise so callers never see the rejection. Auth-probe endpoints are exempt —
// they handle their own state.
const AUTH_PROBE_PATHS = /\/auth\/(session|me|login|register|refresh|logout|google\/session)/;

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !AUTH_PROBE_PATHS.test(url) && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("bi-session-expired"));
      // Swallow — caller will never see this rejection.
      return new Promise(() => {});
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Trigger a "hand-off" of an API-served file (PDF, CSV, XLSX, …) that
 * NEVER navigates the installed PWA away.
 *
 * Strategy (in preference order):
 *   1. Fetch the file as a `Blob` via the same axios instance (auth cookie
 *      flows automatically, credentials preserved).
 *   2. If the OS supports Web Share with files (iOS 15+ / Android),
 *      call `navigator.share({ files })` inline. The share sheet returns
 *      focus to the PWA on Cancel or Send — the tradie is never stranded
 *      in Safari's inline PDF viewer with no back button.
 *   3. Otherwise (desktop, older Android), create a blob `ObjectURL` and
 *      click a synthetic `<a download>`. This triggers the browser's
 *      native Save-As without ever setting `window.location`, so the
 *      current tab / PWA process stays put.
 *
 * The old `<a target="_blank">` iOS branch used to hijack the PWA session
 * and drop the tradie into Safari's PDF viewer with no route back to the
 * app — replaced entirely.
 */
export async function downloadFile(path, filename) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const safeName = filename || "buildintel.pdf";
  // 1. Fetch as blob — credentials:'include' pulls the auth cookie.
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const mime = blob.type || (safeName.toLowerCase().endsWith(".pdf")
    ? "application/pdf"
    : "application/octet-stream");
  const file = new File([blob], safeName, { type: mime });

  // 2. Web Share — preferred on mobile. `canShare({files})` guards for
  //    both API presence and file-share support (iOS 14 Safari has share
  //    but not files). AbortError = user cancelled → stay put.
  if (
    typeof navigator !== "undefined"
    && typeof navigator.canShare === "function"
    && navigator.canShare({ files: [file] })
    && typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({ files: [file], title: safeName });
      return;
    } catch (err) {
      if (err && err.name === "AbortError") return;  // user tapped Cancel
      // fall through to blob-URL save
    }
  }

  // 3. Blob-URL save — never uses target="_blank" or window.location,
  //    so the PWA process is never replaced. iOS 13.4+ honours the
  //    `download` attribute for same-origin URLs; desktop + Android
  //    fire native Save As.
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = safeName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Release memory once the browser has picked up the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}
